import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { EmailComposeData } from '@/types/email';
import { toast } from 'sonner';
import { useCurrentOrgId } from '../auth';

/** Limite da API Nylas para anexos em JSON: 3 MB (https://developer.nylas.com/docs/v3/email/attachments/) */
const NYLAS_MAX_ATTACHMENTS_BYTES = 3 * 1024 * 1024;
/** Limite para fluxo multipart (upload em storage + Nylas multipart): 25 MB */
const NYLAS_MULTIPART_MAX_BYTES = 25 * 1024 * 1024;

const EMAIL_ATTACHMENTS_BUCKET = 'files-pub';
const EMAIL_ATTACHMENTS_PREFIX = 'email-attachments';

export const useNylasSendEmail = () => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { orgId } = useCurrentOrgId();
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;

  const sendEmail = useCallback(async (emailData: EmailComposeData & { replyToMessageId?: string; account_id?: string }) => {
    setSending(true);
    setError(null);

    if (!orgIdRef.current) {
      toast.error('Organização não selecionada. Selecione uma organização para enviar o email.');
      setSending(false);
      return { success: false, error: 'Organização não selecionada' };
    }

    try {
      const hasAttachments = emailData.attachments && emailData.attachments.length > 0;
      const totalAttachmentBytes = hasAttachments
        ? emailData.attachments!.reduce((sum, file) => sum + file.size, 0)
        : 0;

      // Limite multipart Nylas: 25 MB
      if (hasAttachments && totalAttachmentBytes > NYLAS_MULTIPART_MAX_BYTES) {
        const totalMB = (totalAttachmentBytes / (1024 * 1024)).toFixed(2);
        const msg = `O tamanho total dos anexos não pode ultrapassar 25 MB. Total atual: ${totalMB} MB.`;
        toast.error(msg);
        setSending(false);
        return { success: false, error: msg };
      }

      const useMultipartFlow = hasAttachments && totalAttachmentBytes > NYLAS_MAX_ATTACHMENTS_BYTES;
      const body: Record<string, unknown> = {
        org_id: orgIdRef.current,
        account_id: emailData.account_id ?? undefined,
        to: emailData.to,
        cc: emailData.cc,
        bcc: emailData.bcc,
        subject: emailData.subject,
        body: emailData.body,
        replyToMessageId: emailData.replyToMessageId,
        tracking_enabled: emailData.tracking_enabled ?? true,
        scheduled_at: emailData.scheduled_at ?? undefined,
        links: emailData.links ?? [],
      };

      if (useMultipartFlow) {
        // Fluxo para anexos grandes (3 MB < total ≤ 25 MB): upload no storage e envio por attachmentRefs
        const requestId = crypto.randomUUID();
        const basePath = `${EMAIL_ATTACHMENTS_PREFIX}/${orgIdRef.current}/${requestId}`;
        const attachmentRefs: { bucket: string; path: string; filename: string; content_type: string; size: number }[] = [];

        for (const file of emailData.attachments!) {
          const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
          const path = `${basePath}/${safeName}`;
          const { error: uploadError } = await supabase.storage
            .from(EMAIL_ATTACHMENTS_BUCKET)
            .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: true });

          if (uploadError) {
            console.error('Upload de anexo para storage falhou:', uploadError);
            toast.error('Falha ao enviar anexos. Tente novamente.');
            setSending(false);
            return { success: false, error: uploadError.message };
          }
          attachmentRefs.push({
            bucket: EMAIL_ATTACHMENTS_BUCKET,
            path,
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            size: file.size,
          });
        }
        body.attachmentRefs = attachmentRefs;
      } else if (hasAttachments) {
        // Fluxo atual para anexos ≤ 3 MB: base64 no body
        const attachments = await Promise.all(
          emailData.attachments!.map(async (file) => {
            const base64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1] ?? '');
              };
              reader.readAsDataURL(file);
            });
            return {
              content: base64,
              content_type: file.type,
              filename: file.name,
              size: file.size,
            };
          })
        );
        body.attachments = attachments;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Sessão expirada. Faça login novamente.');
        setSending(false);
        return { success: false, error: 'Não autenticado' };
      }

      // Enviar token no body também (fallback quando o gateway remove o header Authorization)
      body.auth_token = session.access_token;

      const { data, error } = await supabase.functions.invoke('nylas-send-email', {
        body,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error sending email:', error);
        
        if (error.needsRefresh) {
          toast.error('Sua sessão expirou. Por favor, reconecte sua conta de email.');
          setError('Token expirado - reconecte sua conta');
          return { success: false, error: 'Token expirado' };
        }
        
        throw error;
      }

      // Resposta 4xx da edge function vem em data com error
      if (data?.error) {
        const msg = typeof data.error === 'string' ? data.error : data.details ?? 'Erro ao enviar email';
        toast.error(msg);
        setError(msg);
        setSending(false);
        return { success: false, error: msg };
      }

      console.log('Email sent successfully:', data);
      toast.success(data.message || 'Email enviado com sucesso!');
      
      return { 
        success: true, 
        messageId: data.messageId,
        emailMessageId: data.emailMessageId as string | undefined,
        message: data.message 
      };
      
    } catch (err: any) {
      console.error('Failed to send email:', err);
      const errorMessage = err.message || 'Erro ao enviar email';
      setError(errorMessage);
      toast.error(errorMessage);
      
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      setSending(false);
    }
  }, []);

  return {
    sendEmail,
    sending,
    error
  };
};